import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import * as Yup from "yup";
import EmployerViewController from "../../redux/actions/EmployerApiAction";
import { IMAGE_URL } from "../../services/api";
import ProtectedImage from "../../components/ProtectedImage";
import FormDrawer from "../../components/layout/FormDrawer";
import MobileSelectField from "../../components/mobile/MobileSelectField";
import { BRAND, primaryButtonSx, inputSx, mobileInputSx } from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  scope: Yup.string().required("Project scope is required"),
  start_date: Yup.date().required("Start Date is required"),
  end_date: Yup.date().required("End Date is required"),
});

const SectionCard = ({ title, isMobile, children, mb = 1.5 }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "12px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? mb : 3,
    }}
  >
    {title && (
      <Typography
        variant="subtitle2"
        sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.5 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

function ProjectFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2.5;

  const { addProjects, updateProject } = ProjectViewController();
  const { getAllEmployeeEmployer } = EmployerViewController();
  const { employers } = useSelector((state) => state.employer);
  const { isLoding } = useSelector((state) => state.project);

  const isEdit = Boolean(initialData?.id);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    project_leader: "",
    scope: "",
    start_date: "",
    end_date: "",
    project_budget: "",
    is_active: false,
    attachments: [],
  });

  const leaderOptions = (employers || []).map((emp) => ({
    label: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
    value: emp.id,
  }));

  useEffect(() => {
    if (show && (!employers || employers.length === 0)) {
      dispatch(getAllEmployeeEmployer());
    }
  }, [show, dispatch, employers]);

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setInitialValues({
        title: initialData.title || "",
        description: initialData.description || "",
        scope: initialData.scope || "",
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        project_budget: initialData.project_budget || "",
        project_leader: initialData.project_leader?.id || "",
        is_active: initialData.is_active ?? false,
        attachments: [],
      });
      setExistingAttachments(initialData.attachments || []);
    } else {
      setInitialValues({
        title: "",
        description: "",
        scope: "",
        start_date: "",
        end_date: "",
        project_budget: "",
        project_leader: "",
        is_active: false,
        attachments: [],
      });
      setExistingAttachments([]);
    }
  }, [show, initialData]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key !== "attachments") formData.append(key, value);
    });
    if (values.attachments?.length > 0) {
      Array.from(values.attachments).forEach((file) => {
        formData.append("attachments", file);
      });
    }

    try {
      const action = isEdit
        ? updateProject(initialData.id, formData)
        : addProjects(formData);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (err) {
      console.error("Submission Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFooter = (isSaving, submitForm) =>
    isMobile ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          fullWidth
          sx={{
            ...primaryButtonSx,
            minHeight: 48,
            borderRadius: "12px",
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
              ? "Update"
              : "Submit"}
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSaving}
          fullWidth
          sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
      </Box>
    ) : (
      <>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          sx={{
            ...primaryButtonSx,
            minWidth: 120,
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
              ? "Update"
              : "Submit"}
        </Button>
      </>
    );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
    >
      {({
        setFieldValue,
        errors,
        touched,
        values,
        handleChange,
        handleBlur,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              title={isEdit ? "Edit Project" : "Add Project"}
              subtitle={
                isEdit
                  ? "Update project details and timeline"
                  : "Create a new project for your team"
              }
              width={{ xs: "100%", sm: 520, md: 560 }}
              footer={renderFooter(isSaving, submitForm)}
            >
              <SectionCard title="Project details" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={isMobile ? 3 : 4}
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Scope"
                      name="scope"
                      value={values.scope}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.scope && Boolean(errors.scope)}
                      helperText={touched.scope && errors.scope}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Timeline" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      type="date"
                      label="Start Date"
                      name="start_date"
                      InputLabelProps={{ shrink: true }}
                      value={values.start_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.start_date && Boolean(errors.start_date)}
                      helperText={touched.start_date && errors.start_date}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      type="date"
                      label="End Date"
                      name="end_date"
                      InputLabelProps={{ shrink: true }}
                      value={values.end_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.end_date && Boolean(errors.end_date)}
                      helperText={touched.end_date && errors.end_date}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Budget & leader" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Budget"
                      name="project_budget"
                      value={values.project_budget}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Project Leader"
                        value={values.project_leader}
                        onChange={(val) => setFieldValue("project_leader", val)}
                        options={leaderOptions}
                        placeholder="Select leader"
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={employers || []}
                        getOptionLabel={(emp) =>
                          `${emp.first_name || ""} ${emp.last_name || ""}`.trim()
                        }
                        value={
                          (employers || []).find(
                            (e) => e.id === values.project_leader
                          ) || null
                        }
                        onChange={(_event, newValue) =>
                          setFieldValue("project_leader", newValue ? newValue.id : "")
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Project Leader" sx={fieldSx} />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Attachments" isMobile={isMobile}>
                {isEdit && existingAttachments.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: BRAND.navy }}>
                      Existing Attachments
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {existingAttachments.map((url, index) => {
                        const fullUrl = `${IMAGE_URL}${url}`;
                        const isPDF = fullUrl.toLowerCase().endsWith(".pdf");
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fullUrl);
                        if (isImage) {
                          return (
                            <ProtectedImage
                              key={index}
                              path={fullUrl}
                              alt={`Attachment ${index + 1}`}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: `1px solid ${BRAND.border}`,
                              }}
                              onClick={() => window.open(fullUrl, "_blank")}
                            />
                          );
                        }
                        return (
                          <Button
                            key={index}
                            size="small"
                            onClick={() => window.open(fullUrl, "_blank")}
                          >
                            {isPDF ? `PDF ${index + 1}` : `File ${index + 1}`}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    borderColor: BRAND.orange,
                    color: BRAND.orange,
                    textTransform: "none",
                    borderRadius: "10px",
                  }}
                >
                  Upload Files
                  <input
                    name="attachments"
                    type="file"
                    multiple
                    hidden
                    accept=".pdf, image/*"
                    onChange={(event) => {
                      setFieldValue("attachments", event.currentTarget.files);
                    }}
                  />
                </Button>
                <Typography variant="caption" sx={{ color: BRAND.muted, display: "block", mt: 1 }}>
                  You can only upload PDF and Image files.
                </Typography>
                {values.attachments?.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
                    {Array.from(values.attachments).map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          border: `1px solid ${BRAND.border}`,
                          borderRadius: "10px",
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: BRAND.navy,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </SectionCard>

              <SectionCard title="Status" isMobile={isMobile} mb={0}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_active"
                      checked={values.is_active}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Is Active"
                />
              </SectionCard>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default ProjectFormModal;
